import React, {FC, useContext} from 'react';
import {Typography} from 'onboarding-components';
import clsx from 'clsx';

import {Trans} from 'common/i18n';
import {useTranslation} from 'common/i18n/hooks';
import common from 'iron/common/onboarding/immersive-onboarding/steps/common.module.styl';
import {TabContainer, TabPanel} from 'iron/common/onboarding/immersive-onboarding/components/content-tabs';
import {CalloutBlock} from 'iron/common/onboarding/immersive-onboarding/components/callout-block';
import {CodeBox} from 'iron/common/onboarding/immersive-onboarding/components/code-box';
import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding/assets';
import {eligibleForAiWizard, handleCompleteStep} from 'common/onboarding/immersive-onboarding/utils';
import {TrackingEvent, TrackingProperty, trackOnboardingEvent} from 'common/onboarding/immersive-onboarding/tracking';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {SwitchChangeEvent, SwitchReact} from 'common/components/switch';
import {SvgIconReact} from 'common/components/svg-icon';
import {AiEventType, AiEventTypeMapping, SetupGuideContext, Step} from 'common/onboarding/immersive-onboarding/types';

import style from './InstallGuide.module.styl';

interface SwitchLinkProps {
  className?: string;
}

const SwitchLinks: FC<SwitchLinkProps> = (props) => {
  const {
    setupGuideInfo,
    setSetupGuideInfo,
    setStep,
    useSessionReplay,
    setUseSessionReplay,
    useAutocapture,
    setUseAutocapture,
  } = useContext(SetupGuideContext);
  const {t} = useTranslation();

  const handleAutocaptureSwitch = (ev: SwitchChangeEvent) => {
    setUseAutocapture(ev.detail.on);
    handleCompleteStep({
      setupGuideInfo,
      setSetupGuideInfo,
      setStep,
      completedStep: Step.GetStarted,
      nextStep: Step.Connect,
      trackingEvent: TrackingEvent.ToggleAutocapture,
      trackingData: {
        useAutocapture: ev.detail.on,
      },
    });
  };

  const handleSessionReplaySwitch = (ev: SwitchChangeEvent) => {
    setUseSessionReplay(ev.detail.on);
    handleCompleteStep({
      setupGuideInfo,
      setSetupGuideInfo,
      setStep,
      completedStep: Step.GetStarted,
      nextStep: Step.Connect,
      trackingEvent: TrackingEvent.ToggleSessionReplay,
      trackingData: {
        useSessionReplay: ev.detail.on,
      },
    });
  };
  return (
    <div className={clsx(props.className, style.switchLinks)}>
      {/* Autocapture switch */}
      <div className={style.switchLink}>
        <SwitchReact on={useAutocapture} onChange={(ev) => handleAutocaptureSwitch(ev as SwitchChangeEvent)} />
        <a
          href='https://docs.mixpanel.com/docs/tracking-methods/autocapture'
          target='_blank'
          rel='noreferrer'
          className={style.switchLinkAction}
          onClick={() => {
            trackOnboardingEvent(TrackingEvent.LinkClicked, {
              [TrackingProperty.LinkSource]: TrackingEvent.AutocaptureDocLink,
            });
          }}
        >
          <Typography className={clsx(common.mt0, style.switchLinkText)} variant='captionXs'>
            {t(`onboarding.install-js-sdk.toggles.autocapture`)}
          </Typography>
          <SvgIconReact icon='externalLink' />
        </a>
      </div>

      {/* Session Replay switch */}
      <div className={style.switchLink}>
        <SwitchReact on={useSessionReplay} onChange={(ev) => handleSessionReplaySwitch(ev as SwitchChangeEvent)} />
        <a
          href='https://docs.mixpanel.com/docs/session-replay'
          target='_blank'
          rel='noreferrer'
          className={style.switchLinkAction}
          onClick={() => {
            trackOnboardingEvent(TrackingEvent.LinkClicked, {
              [TrackingProperty.LinkSource]: TrackingEvent.SessionReplayDocLink,
            });
          }}
        >
          <Typography className={clsx(common.mt0, style.switchLinkText)} variant='captionXs'>
            {t(`onboarding.install-js-sdk.toggles.session-replay`)}
          </Typography>
          <SvgIconReact icon='externalLink' />
        </a>
      </div>
    </div>
  );
};

const ContentBlockPrecisionTracking: FC<{className?: string}> = ({className}) => {
  const {t} = useTranslation();

  return (
    <>
      <div className={clsx(common.headerRow, className)}>
        <Typography className={common.mt0} variant='bodySm' weight='bold'>
          {t(`onboarding.connect.configure-tracking`)}
        </Typography>
        <div className={common.label}>
          <Typography variant='captionXs'>{t(`onboarding.install-js-sdk.configure-subheading`)}</Typography>
        </div>
      </div>
      <Typography variant='captionSm'>
        <Trans
          i18nKey={`onboarding.connect.configure-tracking-description`}
          t={t}
          components={{
            docLink: (
              <a
                href='https://docs.mixpanel.com/docs/tracking-methods/autocapture#autocapture-vs-precision-tracking'
                target='_blank'
                rel='noreferrer'
              />
            ),
          }}
        />
      </Typography>
    </>
  );
};

export const InstallGuide: FC = () => {
  const {useAutocapture, useSessionReplay, setupGuideInfo, aiEventType, setAiEventType} = useContext(SetupGuideContext);
  const {t} = useTranslation();

  const aiEventTypes = [
    {value: AiEventType.DefaultEvents, label: AiEventType.DefaultEvents},
    {value: AiEventType.Ecommerce, label: AiEventType.Ecommerce},
    {value: AiEventType.AI, label: AiEventType.AI},
  ];

  const getAiEventTypeSections = () => {
    return aiEventTypes.map((type) => ({
      label: type.label,
      icon: `event`,
    }));
  };

  // hidden behind feature flag for now
  const useAITab = (
    <TabPanel heading='Use AI'>
      {/*heading={t(`onboarding.install-js-sdk.tabs.snippet`)}>*/}
      <Typography className={common.mt16} variant='bodySm' weight='bold'>
        {t(`onboarding.install-js-sdk.initialize-AI-heading`)}
      </Typography>
      <Typography variant='captionSm'>{t(`onboarding.install-js-sdk.initialize-AI-description`)}</Typography>
      <Typography className={common.mt16} variant='bodySm' weight='bold'>
        {t(`onboarding.install-js-sdk.event-type-selector-label`)}
      </Typography>

      <div className={clsx(common.mt8)}>
        <SelectReact
          theme={SelectTheme.Secondary}
          menuWidth={-1}
          selectedOption={aiEventType}
          onChange={(ev: TODO) => {
            setAiEventType(ev.detail.value);
          }}
          sections={[
            {
              items: getAiEventTypeSections(),
            },
          ]}
        />
      </div>
      <div className={style.aiPromptHeader}>
        <Typography className={common.mt16} variant='bodySm' weight='bold'>
          {t(`onboarding.install-js-sdk.llm-prompt`)}
        </Typography>
        <div className={style.switchLinksWithAiContent}>
          <SwitchLinks className={common.mt16} />
        </div>
      </div>
      <div className={common.mb16}>
        {/* eslint-disable */}
        <CodeBox
          code={`Let's set up Mixpanel. 
1. Run the following command; don't write any code until it completes:
   npx @mixpanel/mixpanel-wizard --sdk javascript --events ${AiEventTypeMapping[aiEventType]} ${useAutocapture ? `--autocapture ` : ``}${useSessionReplay ? `--sessionreplay ` : ``}-t ${setupGuideInfo.projectToken ? setupGuideInfo.projectToken : `YOUR_PROJECT_TOKEN`}
2. After that command finishes, set up mixpanel by following the steps in MIXPANEL_INSTALLATION_INSTRUCTIONS.txt. Make sure to follow the global rules specified in the MIXPANEL_INSTALLATION_INSTRUCTIONS.txt.
3. After completing the steps in the installation guide, delete the MIXPANEL_INSTALLATION_INSTRUCTIONS.txt`}
        />
        {/* eslint-enable */}
      </div>
      {!useAutocapture && (
        <CalloutBlock
          icon={IMMERSIVE_ASSETS.toggles}
          iconAltText={t(`onboarding.install-js-sdk.disabled-autocapture-heading`)}
          className={common.mt32}
        >
          <Typography className={common.mb4} variant='captionSm' weight='bold'>
            {t(`onboarding.install-js-sdk.disabled-autocapture-heading`)}
          </Typography>
          <Typography variant='captionXs'>{t(`onboarding.install-js-sdk.disabled-autocapture-description`)}</Typography>
        </CalloutBlock>
      )}
      <ContentBlockPrecisionTracking className={clsx(common.mt32)} />
    </TabPanel>
  );

  const mapTabIndexToTrackingEvent = (index: number): TrackingEvent => {
    // TODO: Add AI tab tracking event when released
    return (
      new Map<number, TrackingEvent>([
        [0, TrackingEvent.SnippetLinkClick],
        [1, TrackingEvent.NpmTabClick],
        [2, TrackingEvent.YarnTabClick],
      ]).get(index) || TrackingEvent.SnippetLinkClick
    );
  };

  return (
    <TabContainer
      defaultActive={0}
      onTabChange={(i) => {
        trackOnboardingEvent(TrackingEvent.TabChanged, {
          [TrackingProperty.CurrentStep]: Step.Connect,
          [TrackingProperty.TabName]: mapTabIndexToTrackingEvent(i),
        });
      }}
    >
      {/* Only render AI tab if eligible */}
      {eligibleForAiWizard() && useAITab}
      <TabPanel heading={t(`onboarding.install-js-sdk.tabs.snippet`)}>
        <Typography className={common.mt16} variant='bodySm' weight='bold'>
          {t(`onboarding.install-js-sdk.initialize-heading`)}
        </Typography>
        <Typography variant='captionSm'>{t(`onboarding.install-js-sdk.initialize-description`)}</Typography>

        <SwitchLinks className={common.mt16} />

        <div className={common.mb16}>
          {/* eslint-disable */}
          <CodeBox
            code={`<!-- Paste this right before your closing </head> tag -->
<script type="text/javascript">
  (function(e,c){if(!c.__SV){var l,h;window.mixpanel=c;c._i=[];c.init=function(q,r,f){function t(d,a){var g=a.split(".");2==g.length&&(d=d[g[0]],a=g[1]);d[a]=function(){d.push([a].concat(Array.prototype.slice.call(arguments,0)))}}var b=c;"undefined"!==typeof f?b=c[f]=[]:f="mixpanel";b.people=b.people||[];b.toString=function(d){var a="mixpanel";"mixpanel"!==f&&(a+="."+f);d||(a+=" (stub)");return a};b.people.toString=function(){return b.toString(1)+".people (stub)"};l="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders start_session_recording stop_session_recording people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");
  for(h=0;h<l.length;h++)t(b,l[h]);var n="set set_once union unset remove delete".split(" ");b.get_group=function(){function d(p){a[p]=function(){b.push([g,[p].concat(Array.prototype.slice.call(arguments,0))])}}for(var a={},g=["get_group"].concat(Array.prototype.slice.call(arguments,0)),m=0;m<n.length;m++)d(n[m]);return a};c._i.push([q,r,f])};c.__SV=1.2;var k=e.createElement("script");k.type="text/javascript";k.async=!0;k.src="undefined"!==typeof MIXPANEL_CUSTOM_LIB_URL?MIXPANEL_CUSTOM_LIB_URL:"file:"===
  e.location.protocol&&"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js".match(/^\\/\\//)?"https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js":"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";e=e.getElementsByTagName("script")[0];e.parentNode.insertBefore(k,e)}})(document,window.mixpanel||[])
  
  mixpanel.init('${setupGuideInfo.projectToken ? setupGuideInfo.projectToken : `YOUR_PROJECT_TOKEN`}', {
    autocapture: ${useAutocapture ? `true` : `false`}, 
    record_sessions_percent: ${useSessionReplay ? 100 : 0}
  })
</script>`}
          />
          {/* eslint-enable */}
        </div>

        {!useAutocapture && (
          <CalloutBlock
            icon={IMMERSIVE_ASSETS.toggles}
            iconAltText={t(`onboarding.install-js-sdk.disabled-autocapture-heading`)}
            className={common.mt32}
          >
            <Typography className={common.mb4} variant='captionSm' weight='bold'>
              {t(`onboarding.install-js-sdk.disabled-autocapture-heading`)}
            </Typography>
            <Typography variant='captionXs'>
              {t(`onboarding.install-js-sdk.disabled-autocapture-description`)}
            </Typography>
          </CalloutBlock>
        )}

        <ContentBlockPrecisionTracking className={clsx(common.mt32)} />
      </TabPanel>

      <TabPanel heading={t(`onboarding.install-js-sdk.tabs.npm`)}>
        <Typography className={clsx(common.mt16, common.mb4)} variant='bodySm' weight='bold'>
          {t(`onboarding.install-js-sdk.command-line-heading`)}
        </Typography>
        <Typography variant='captionSm'>{t(`onboarding.install-js-sdk.command-line-description`)}</Typography>
        <CodeBox code={`npm install --save mixpanel-browser`} />

        <Typography className={common.mt16} variant='bodySm' weight='bold'>
          {t(`onboarding.install-js-sdk.initialize-heading`)}
        </Typography>
        <Typography variant='captionSm'>{t(`onboarding.install-js-sdk.initialize-description`)}</Typography>

        <SwitchLinks className={common.mt16} />

        <div className={common.mb16}>
          {/* eslint-disable */}
          <CodeBox
            code={`//Import Mixpanel SDK
import mixpanel from "mixpanel-browser";

// Create an instance of the Mixpanel object, your token is already added to this snippet
mixpanel.init('${setupGuideInfo.projectToken ? setupGuideInfo.projectToken : `YOUR_PROJECT_TOKEN`}', {
  autocapture: ${useAutocapture ? `true` : `false`}, 
  record_sessions_percent: ${useSessionReplay ? 100 : 0}
})`}
          />
          {/* eslint-enable */}
        </div>

        {!useAutocapture && (
          <CalloutBlock
            icon={IMMERSIVE_ASSETS.toggles}
            iconAltText={t(`onboarding.install-js-sdk.disabled-autocapture-heading`)}
            className={common.mt32}
          >
            <Typography className={common.mb4} variant='captionSm' weight='bold'>
              {t(`onboarding.install-js-sdk.disabled-autocapture-heading`)}
            </Typography>
            <Typography variant='captionXs'>
              {t(`onboarding.install-js-sdk.disabled-autocapture-description`)}
            </Typography>
          </CalloutBlock>
        )}

        <ContentBlockPrecisionTracking className={clsx(common.mt16, common.mb4)} />
      </TabPanel>

      <TabPanel heading={t(`onboarding.install-js-sdk.tabs.yarn`)}>
        <Typography className={clsx(common.mt16, common.mb4)} variant='bodySm' weight='bold'>
          {t(`onboarding.install-js-sdk.command-line-heading`)}
        </Typography>
        <Typography variant='captionSm'>{t(`onboarding.install-js-sdk.command-line-description`)}</Typography>

        <CodeBox code={`yarn add mixpanel-browser`} />

        <Typography className={common.mt16} variant='bodySm' weight='bold'>
          {t(`onboarding.install-js-sdk.initialize-heading`)}
        </Typography>
        <Typography variant='captionSm'>{t(`onboarding.install-js-sdk.initialize-description`)}</Typography>

        <SwitchLinks className={common.mt16} />

        <div className={common.mb16}>
          {/* eslint-disable */}
          <CodeBox
            code={`//Import Mixpanel SDK
import mixpanel from "mixpanel-browser";

// Create an instance of the Mixpanel object, your token is already added to this snippet
mixpanel.init('${setupGuideInfo.projectToken ? setupGuideInfo.projectToken : `YOUR_PROJECT_TOKEN`}', {
  autocapture: ${useAutocapture ? `true` : `false`}, 
  record_sessions_percent: ${useSessionReplay ? 100 : 0}
});`}
          />
          {/* eslint-enable */}
        </div>

        {!useAutocapture && (
          <CalloutBlock
            icon={IMMERSIVE_ASSETS.toggles}
            iconAltText={t(`onboarding.install-js-sdk.disabled-autocapture-heading`)}
            className={common.mt32}
          >
            <Typography className={common.mb4} variant='captionSm' weight='bold'>
              {t(`onboarding.install-js-sdk.disabled-autocapture-heading`)}
            </Typography>
            <Typography variant='captionXs'>
              {t(`onboarding.install-js-sdk.disabled-autocapture-description`)}
            </Typography>
          </CalloutBlock>
        )}

        <ContentBlockPrecisionTracking className={clsx(common.mt16, common.mb4)} />
      </TabPanel>
    </TabContainer>
  );
};
